interface AuthUser {
  id: string
  kindeId: string
  email: string
  firstName: string | null
  lastName: string | null
  name: string
  picture: string | null
  avatar?: string
  roles: string[]
  permissions: string[]
  createdAt: Date
  updatedAt: Date
}

interface AuthComposableState {
  user: AuthUser | null
  loggedIn: boolean
  loading: boolean
}

export const useAuthorization = () => {
  const authState = useState<AuthComposableState>('auth', () => ({
    user: null,
    loggedIn: false,
    loading: true
  }))

  const config = useRuntimeConfig()

  // Initialize auth state with optimized approach
  const initialize = async () => {
    try {
      authState.value.loading = true

      // Use $fetch for better performance and error handling
      const user = await $fetch<AuthUser | null>('/api/auth/me').catch(() => null)

      if (user) {
        authState.value.user = user
        authState.value.loggedIn = true
      } else {
        authState.value.user = null
        authState.value.loggedIn = false
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error)
      authState.value.user = null
      authState.value.loggedIn = false
    } finally {
      authState.value.loading = false
    }
  }

  // Login with Kinde
  const login = async (redirectTo?: string) => {
    if (redirectTo) {
      // Store redirect URL for after login
      await navigateTo(`/api/auth/login?org_code=${config.public.kindeOrgCode}&redirect=${encodeURIComponent(redirectTo)}`)
    } else {
      await navigateTo(`/api/auth/login?org_code=${config.public.kindeOrgCode}`)
    }
  }

  // Register with Kinde (only if registration is enabled)
  const register = async (redirectTo?: string) => {
    const config = useRuntimeConfig()

    if (!config.public.registrationEnabled) {
      console.warn('Registration is disabled')
      return
    }

    if (redirectTo) {
      // Store redirect URL for after registration
      await navigateTo(`/api/auth/register?redirect=${encodeURIComponent(redirectTo)}`)
    } else {
      await navigateTo('/api/auth/register')
    }
  }

  // Logout
  const logout = async () => {
    try {
      const response = await $fetch<{ logoutUrl: string }>('/api/auth/logout', { method: 'POST' })
      authState.value.user = null
      authState.value.loggedIn = false

      // Redirect to Kinde logout URL
      if (response.logoutUrl) {
        await navigateTo(response.logoutUrl, { external: true })
      } else {
        await navigateTo('/')
      }
    } catch (error) {
      console.error('Logout failed:', error)
      // Fallback: clear state and redirect anyway
      authState.value.user = null
      authState.value.loggedIn = false
      await navigateTo('/')
    }
  }

  // Check if user has a specific role
  const hasRole = (role: string): boolean => {
    const userRoles = authState.value.user?.roles
    return Array.isArray(userRoles) ? userRoles.includes(role) : false
  }

  // Check if user has any of the specified roles
  const hasAnyRole = (roles: string[]): boolean => {
    const result = roles.some(role => hasRole(role))
    return result
  }

  // Check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    const userPermissions = authState.value.user?.permissions
    return Array.isArray(userPermissions) ? userPermissions.includes(permission) : false
  }

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(permission => hasPermission(permission))
  }

  // Check if user is admin
  const isAdmin = computed(() => hasRole('admin'))

  // Check if user can manage portfolios
  const canManagePortfolios = computed(() =>
    hasAnyRole(['admin', 'portfolio_admin']) ||
    hasAnyPermission(['write:portfolio'])
  )

  // Check if registration is enabled
  const isRegistrationEnabled = computed(() => {
    const config = useRuntimeConfig()
    return config.public.registrationEnabled
  })

  return {
    // State
    user: readonly(computed(() => authState.value.user)),
    loggedIn: readonly(computed(() => authState.value.loggedIn)),
    loading: readonly(computed(() => authState.value.loading)),

    // Actions
    initialize,
    login,
    register,
    logout,

    // Role & Permission checks
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,

    // Computed helpers
    isAdmin,
    canManagePortfolios,
    isRegistrationEnabled
  }
}
